import type { EntityState } from '@reduxjs/toolkit';
import type {
  Content,
  ContentStatuses,
  GameType,
  Image,
  LanguageCode,
  Languages,
  Media,
  MultilingualLabel,
  UserEngagement,
  WithCampaignContentId,
} from '@sparted/shared-library/business/types';

type RecursiveValidationError<T> = {
  [K in keyof T]?: T[K] extends object ? RecursiveValidationError<T[K]> | string[] : string[];
};

type ValidationErrorForLanguages<T> = Record<keyof typeof Languages, RecursiveValidationError<T> | undefined>;

export type MultiLingualLabelWithId = Partial<MultilingualLabel>;

type Segment = {
  label: string;
  dimensionId: number;
  magicCodes?: string[];
  emailList?: string[];
};

export type ActivityAudience = {
  type: 'all' | 'segmentation' | 'magic-code' | 'email-list' | 'audience';
  groupId: number;
  segments?: Segment[];
  targetedId?: number;
};

export type CampaignScheduling = {
  mode: 'manual' | 'random';
  type: 'flexible' | 'intense';
  launchDate?: string;
  endDate?: string;
  extraDays?: number;
  enabledWeekDays?: number[];
  disabledDates?: string[];
  orderedSessions?: PartialContent[][];
  nbSessions?: number;
  contents?: PartialContent[];
};

export type CampaignSchedulingFromApi = {
  mode: 'manual' | 'random';
  type: 'flexible' | 'intense';
  launchDate?: string;
  endDate?: string;
  extraDays?: number;
  enabledWeekDays?: number[];
  disabledDates?: string[];
  orderedSessions?: WithCampaignContentId<Content>[][];
  nbSessions?: number;
  contents?: WithCampaignContentId<Content>[];
};

export type PartialContentForCampaignForm = {
  id: string;
  campaignContentId?: number;
  contentId: number;
  gameType?: GameType;
  coverImage?: Pick<Image, 'url'>;
  name: MultiLingualLabelWithId;
  identifier: string;
  statuses: ContentStatuses;
  archived: boolean;
};

export type PartialContent = {
  id: number;
  campaignContentId?: number;
  gameType?: GameType;
  coverImage?: Pick<Image, 'url'>;
  name: MultiLingualLabelWithId;
  identifier: string;
  statuses: ContentStatuses;
  archived: boolean;
};

type BreakingNewsScheduling = {
  startDate?: string;
  endDate?: string;
};

type CampaignInformations = {
  id?: number;
  coverImage?: Pick<Image, 'id' | 'url'>;
  name?: MultiLingualLabelWithId;
  description?: MultiLingualLabelWithId;
  disclaimer?: MultiLingualLabelWithId;
  lastSave?: Date;
};

type BreakingNewsInformations = {
  id?: number;
  coverMedia?: Media;
  name?: MultiLingualLabelWithId;
  text?: MultiLingualLabelWithId;
  description?: MultiLingualLabelWithId;
  isLongText?: boolean;
  lastSave?: Date;
};

type BreakingNewsNotification = {
  isRecurringNotification: boolean;
  isNotificationActive: boolean;
  notificationTime: string;
};
type ActivityStatus = 'draft' | 'inactive' | 'archived' | 'upcoming' | 'live' | 'finished';

export type Campaign = {
  id: number;
  type: 'campaign';
  informations: CampaignInformations;
  scheduling: CampaignScheduling;
  audiences?: ActivityAudience[];
  status: ActivityStatus;
  languages: LanguageCode[];
  userEngagement?: UserEngagement;
};

export type CampaignFromApi = {
  id: number;
  type: 'campaign';
  informations: CampaignInformations;
  scheduling: CampaignSchedulingFromApi;
  audiences?: ActivityAudience[];
  status: ActivityStatus;
  languages: LanguageCode[];
};

export type BreakingNews = {
  id: number;
  type: 'breaking-news';
  informations: BreakingNewsInformations;
  scheduling?: BreakingNewsScheduling;
  notification?: BreakingNewsNotification;
  audiences?: ActivityAudience[];
  status: ActivityStatus;
  languages: LanguageCode[];
  userEngagement?: UserEngagement;
};

type ActivityApiError = {
  message: string;
  statusCode: number;
  errorCode?: string;
  params?: ActivityValidationError;
};

export const isActivityApiError = (error: unknown): error is ActivityApiError =>
  typeof error === 'object' && error !== null && 'message' in error && 'statusCode' in error;

type CombinedValidationError<T> = ValidationErrorForLanguages<T> & RecursiveValidationError<T>;

export type ActivityValidationError = CombinedValidationError<Activity>;

export type Activity = Campaign | BreakingNews;

export type SearchActivityArgs = {
  offset: number;
  limit: number;
  query?: {
    status?: ActivityStatus;
    type?: 'campaign' | 'breaking-news';
    languages?: LanguageCode[];
    audienceGroups?: number[];
    audienceMode?: 'all' | 'group' | 'magic_code' | 'email_list';
    queries?: string[];
  };
};

export type AudienceMode = 'all' | 'magic-code' | 'email-list' | 'audience' | 'groups' | 'none';

export type Audience = {
  mode: AudienceMode;
  code?: string;
  groupNames?: string[];
  campaignName?: { [key in LanguageCode]?: string };
};

export type ActivityDashboardItem = {
  id: number;
  type: 'breaking-news' | 'campaign';
  status: ActivityStatus;
  coverMedia: {
    image?: string;
    video?: {
      type: 'Youtube' | 'Kumullus' | 'api.video';
      url: string;
    };
  };
  title: { [lang in LanguageCode]?: string };
  startDate: string;
  endDate: string;
  languages: LanguageCode[];
  likes: number;
  views: number;
  audience: Audience;
};

export type SearchActivitiesResponse = {
  metadata: {
    total: number;
    offset: number;
    limit: number;
  };
  items: ActivityDashboardItem[];
};

export type ActivityState = EntityState<ActivityDashboardItem> & {
  search: {
    total: number;
    loading: boolean;
    error?: string;
  };
  edition: {
    loading: boolean;
    error: ActivityValidationError | string | null;
    success: string | null;
    saving: boolean;
    campaign?: Campaign;
    breakingNews?: BreakingNews;
    selectedLanguage: LanguageCode;
    locked: boolean;
    dirty: boolean;
    numberTargetedPlayers: number | null;
  };
};

export type DuplicateBreakingNewsId = {
  id: number;
};

export type DuplicateCampaignId = {
  id: number;
};

export enum ActivityEditionResponse {
  ARCHIVE_SUCCESS = 'archive_success_message',
  ARCHIVE_ERROR = 'archive_error_message',
  UNARCHIVE_SUCCESS = 'unarchive_success_message',
  UNARCHIVE_ERROR = 'unarchive_error_message',
}
